import React from "react";
import { CertificationName, ExportCertificationOptions } from "api-client";
import {
  certificationIdsToKeys,
  certificationNameToKey,
  USER_ROLES,
} from "utils";
import { buildingStore } from "store/Building/BuildingStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { buildingSettingsStore } from "features/Building/BuildingSettings/BuildingSettingsStore";
import { conformityStore } from "../store/ConformityStore.ts";

/**
 * Resolves the three certification tiers in one place:
 *  1. Available  -> entitled by the organisation license (`selected_certification_ids`)
 *  2. Enabled    -> available AND toggled on in the building settings (`is_active`)
 *  3. (In-use)   -> `building.certification_id`, handled by useEnsureLicensedCertification
 *
 * Consumers must be wrapped in `observer()` since this reads MobX observables.
 */
export function useCertificationLicense() {
  React.useEffect(() => {
    conformityStore.fetchCertificationData();
  }, []);

  const organisation = sidebarMenuStore.currentOrganisation;
  const selectedIds = organisation?.license?.selected_certification_ids ?? [];
  const certificationOptions = conformityStore.certificationOptions;

  // Entitlement is "known" once BOTH the organisation and the certification
  // catalogue have loaded. We key off the organisation (not its license): the
  // license is embedded in the org payload, so an org with no license is a valid
  // "zero entitlement" state, not an unloaded one. Keying off the license would
  // leave entitlementLoaded permanently false for unlicensed orgs and bypass all
  // gating. Until the org loads, callers must not hide/reset/sanitize state
  // (transient empty entitlement would wipe valid data — load-order race).
  const entitlementLoaded =
    Boolean(organisation) && certificationOptions.length > 0;

  const availableKeys = certificationIdsToKeys(
    selectedIds,
    certificationOptions
  );
  const isAvailable = (key: string) => availableKeys.includes(key);

  const settings = (buildingStore.currentBuilding
    ?.certification_export_settings ?? {}) as Record<
    string,
    ExportCertificationOptions | null | undefined
  >;
  const enabledKeys = availableKeys.filter((key) => settings[key]?.is_active);
  const isEnabled = (key: string) => enabledKeys.includes(key);

  return {
    entitlementLoaded,
    availableKeys,
    isAvailable,
    enabledKeys,
    isEnabled,
    availableCertificationIds: selectedIds,
  };
}

/**
 * Tier 3 guard: if the in-use certification (`building.certification_id`) is no
 * longer available + enabled per the license, silently reset it to "Keine".
 * No toast/dialog. Fires once for an offending state (the reset clears it).
 * Mount once where a building is rendered (e.g. BuildingHeader).
 *
 * Only runs when entitlement is known (avoids wiping a valid cert during the
 * load race) and only for admins (the reset performs a building update).
 */
export function useEnsureLicensedCertification() {
  const { entitlementLoaded, enabledKeys } = useCertificationLicense();
  const certificationOptions = conformityStore.certificationOptions;
  const building = buildingStore.currentBuilding;
  const currentId = building?.certification_id;
  const isAdmin =
    sidebarMenuStore.currentOrganisation?.role === USER_ROLES[0].id;

  React.useEffect(() => {
    if (!entitlementLoaded || !isAdmin) return;
    if (!building?.id || !currentId) return;

    const currentName = certificationOptions.find(
      (c) => c.id === currentId
    )?.name;
    if (!currentName || currentName === CertificationName.Keine) return;

    const currentKey = certificationNameToKey(currentName);
    if (currentKey && enabledKeys.includes(currentKey)) return; // still licensed + enabled

    const keineId = certificationOptions.find(
      (c) => c.name === CertificationName.Keine
    )?.id;
    if (!keineId) return;

    buildingSettingsStore
      .fetchUpdateBuilding(
        { certification_id: keineId, conformity_id: building.conformity_id },
        { silent: true }
      )
      .catch((err) => console.error(err, "useEnsureLicensedCertification"));
  }, [
    entitlementLoaded,
    isAdmin,
    building?.id,
    currentId,
    certificationOptions.length,
    enabledKeys.join(","),
  ]);
}
